/** Matches `.mat-mdc-row:not(.detail-row) { height: 40px }`. */
export const TABLE_ROW_HEIGHT_PX = 40;

/** Rows rendered above and below the viewport. */
export const TABLE_VIRTUAL_BUFFER_ROWS = 20;

export interface TableVirtualExtra {
  /** Extra space (row-detail spacer) inserted after this row index. */
  afterIndex: number;
  height: number;
}

export interface TableVirtualWindowInput {
  rowCount: number;
  rowHeight: number;
  viewportHeight: number;
  scrollTop: number;
  bufferRows: number;
  extras?: readonly TableVirtualExtra[];
}

export interface TableVirtualWindow {
  startIndex: number;
  endIndex: number;
  topPad: number;
  bottomPad: number;
}

export function offsetForRowIndex(
  index: number,
  rowHeight: number,
  extras: readonly TableVirtualExtra[] = []
): number {
  let extra = 0;
  for (const item of extras) {
    if (item.afterIndex < index) {
      extra += item.height;
    }
  }
  return index * rowHeight + extra;
}

export function extrasHeightBefore(
  index: number,
  extras: readonly TableVirtualExtra[]
): number {
  let extra = 0;
  for (const item of extras) {
    if (item.afterIndex < index) {
      extra += item.height;
    }
  }
  return extra;
}

export function extrasHeightOnOrAfter(
  index: number,
  extras: readonly TableVirtualExtra[]
): number {
  let extra = 0;
  for (const item of extras) {
    if (item.afterIndex >= index) {
      extra += item.height;
    }
  }
  return extra;
}

/**
 * Window of row indices to mount, plus spacer pads so the scroll height
 * still matches the full in-memory list.
 */
export function computeTableVirtualWindow(
  input: TableVirtualWindowInput
): TableVirtualWindow {
  const rowCount = Math.max(0, input.rowCount);
  const rowHeight = input.rowHeight > 0 ? input.rowHeight : TABLE_ROW_HEIGHT_PX;
  const extras = input.extras ?? [];

  if (rowCount === 0) {
    return { startIndex: 0, endIndex: 0, topPad: 0, bottomPad: 0 };
  }

  const viewportHeight = Math.max(0, input.viewportHeight);
  const scrollTop = Math.max(0, input.scrollTop);
  const bufferRows = Math.max(0, input.bufferRows);
  const viewportRows = Math.max(1, Math.ceil(viewportHeight / rowHeight) || 1);

  const firstVisible = indexOfOffset(scrollTop, rowCount, rowHeight, extras);
  const startIndex = Math.max(0, firstVisible - bufferRows);
  const endIndex = Math.min(rowCount, firstVisible + viewportRows + bufferRows);

  return {
    startIndex,
    endIndex,
    topPad: startIndex * rowHeight + extrasHeightBefore(startIndex, extras),
    bottomPad:
      (rowCount - endIndex) * rowHeight +
      extrasHeightOnOrAfter(endIndex, extras),
  };
}

function indexOfOffset(
  offset: number,
  rowCount: number,
  rowHeight: number,
  extras: readonly TableVirtualExtra[]
): number {
  let low = 0;
  let high = rowCount;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (offsetForRowIndex(mid, rowHeight, extras) + rowHeight <= offset) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return Math.min(rowCount, low);
}

/** Matches `.row-detail-wrapper-expanded { max-height: min(40rem, 70vh) }`. */
export const EXPANDED_DETAIL_VIEWPORT_CAP_PX = 40 * 16;
/** Floor so a row near the bottom of the table still has a usable scrollport. */
export const MIN_EXPANDED_DETAIL_MAX_HEIGHT_PX = 192;

/**
 * Overflow cap for an expanded row-detail panel.
 * Auto-height tables must not use remaining wrapper space: that value grows as
 * the spacer opens, so a transitioning max-height chases it (slow-motion).
 */
export function computeExpandedRowDetailMaxHeightPx(input: {
  viewportHeight: number;
  remainingInWrapperPx?: number | null;
}): number {
  const viewportCap = Math.round(
    Math.min(
      EXPANDED_DETAIL_VIEWPORT_CAP_PX,
      Math.max(0, input.viewportHeight) * 0.7
    )
  );
  const remaining = input.remainingInWrapperPx;
  if (remaining == null) {
    return Math.max(MIN_EXPANDED_DETAIL_MAX_HEIGHT_PX, viewportCap);
  }
  return Math.max(
    MIN_EXPANDED_DETAIL_MAX_HEIGHT_PX,
    Math.min(remaining, viewportCap || remaining)
  );
}
